/**
 * Materials Handling Guide Page
 * Expert-level guidance on C&D waste materials
 */

import { Link } from 'react-router-dom';

export function MaterialsGuide() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
          ← Back to Home
        </Link>
      </div>

      <h1 className="text-4xl font-bold text-neutral-900 mb-4">
        Construction & Demolition Materials Handling Guide
      </h1>

      <p className="text-lg text-neutral-600 mb-8">
        Expert guidance on diverting C&amp;D waste materials in California &mdash; market conditions, processing methods,
        and practical strategies for achieving 65%+ diversion rates.
      </p>

      <div className="prose prose-neutral max-w-none">
        <section className="mb-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 my-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Understanding Diversion Rates</h3>
            <p className="text-blue-800 mb-3">
              Not all materials are created equal. Diversion rates vary dramatically based on market demand,
              processing infrastructure, and contamination challenges:
            </p>
            <ul className="text-blue-800 space-y-1 text-sm">
              <li><strong>95%+:</strong> Concrete, asphalt, metals (excellent markets)</li>
              <li><strong>70-85%:</strong> Clean wood, cardboard (good markets)</li>
              <li><strong>2-10%:</strong> Drywall, plastics (challenging/limited infrastructure)</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-neutral-900 mb-6 border-b-2 border-primary-500 pb-2">
            Materials by Category
          </h2>

          {/* CONCRETE & MASONRY */}
          <div className="mb-10 border-l-4 border-green-500 pl-6">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Concrete & Masonry
              <span className="ml-3 text-sm font-normal text-green-700 bg-green-100 px-3 py-1 rounded-full">
                95%+ Diversion Rate
              </span>
            </h3>

            <div className="bg-neutral-50 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-neutral-900 mb-2">Common Forms</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                <li>Foundation concrete, slabs, sidewalks</li>
                <li>Concrete masonry units (CMU), bricks, blocks</li>
                <li>Mortar, grout, stucco</li>
                <li>Precast concrete elements</li>
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-900 mb-2">Processing Method</h4>
                <p className="text-sm text-neutral-700">
                  Crushed on-site or at facilities using impact crushers and screens.
                  Rebar removed via magnetic separation. Produces aggregate in various sizes (1.5&quot; minus to fines).
                </p>
              </div>
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-900 mb-2">Primary Markets</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                  <li>Road base and subbase</li>
                  <li>Engineered fill</li>
                  <li>Landscaping aggregate</li>
                  <li>Erosion control riprap</li>
                </ul>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-green-900 mb-2">Best Practices</h4>
              <ul className="space-y-2 text-green-900 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Separate early:</strong> Keep concrete/masonry away from wood, soil, and trash</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Rebar is OK:</strong> Most facilities accept concrete with embedded steel</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Remove contaminants:</strong> Paper, plastic, large wood pieces reduce quality</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Size matters:</strong> Some facilities have maximum piece size limits (typically 2&ndash;3 feet)</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Cost Considerations</h4>
              <p className="text-blue-800 text-sm mb-2">
                <strong>Tipping fees:</strong> $10&ndash;$30/ton (significantly cheaper than landfill at $40&ndash;$80/ton)
              </p>
              <p className="text-blue-800 text-sm">
                <strong>On-site crushing:</strong> May be cost-effective for large demolitions (20,000+ tons).
                Mobile crushers available statewide.
              </p>
            </div>
          </div>

          {/* METALS */}
          <div className="mb-10 border-l-4 border-green-500 pl-6">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Metals
              <span className="ml-3 text-sm font-normal text-green-700 bg-green-100 px-3 py-1 rounded-full">
                95-98% Diversion Rate
              </span>
            </h3>

            <div className="bg-neutral-50 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-neutral-900 mb-2">Common Forms</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-sm font-medium text-neutral-800 mb-1">Ferrous (Magnetic)</p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                    <li>Structural steel, rebar</li>
                    <li>Steel studs, joists, decking</li>
                    <li>Ductwork, conduit, pipe</li>
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-800 mb-1">Non-Ferrous (Non-Magnetic)</p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                    <li>Copper wire, pipe, flashing</li>
                    <li>Aluminum siding, framing, wire</li>
                    <li>Brass fixtures and hardware</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-900 mb-2">Processing Method</h4>
                <p className="text-sm text-neutral-700">
                  Sorted by type, sheared/baled, melted and refined. Magnetic separation for ferrous metals.
                  High-value non-ferrous metals (copper, brass) hand-sorted.
                </p>
              </div>
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-900 mb-2">Scrap Value (2024)</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                  <li>Copper: $3.00-$4.00/lb</li>
                  <li>Brass: $1.50-$2.50/lb</li>
                  <li>Aluminum: $0.50-$0.90/lb</li>
                  <li>Steel/Iron: $100-$200/ton</li>
                </ul>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-green-900 mb-2">Best Practices</h4>
              <ul className="space-y-2 text-green-900 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Separate by type:</strong> Ferrous from non-ferrous maximizes value</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Remove insulation:</strong> Strip wire insulation when possible for higher prices</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Protect from theft:</strong> Secure high-value copper and brass on-site</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Check prices:</strong> Metal values fluctuate - time sales for better returns</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Cost Considerations</h4>
              <p className="text-blue-800 text-sm">
                <strong>You typically get PAID:</strong> Most scrap yards will purchase metals. Larger loads
                command better per-pound rates. Clean, separated metals bring premium pricing.
              </p>
            </div>
          </div>

          {/* ASPHALT */}
          <div className="mb-10 border-l-4 border-green-500 pl-6">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Asphalt Paving & Roofing
              <span className="ml-3 text-sm font-normal text-green-700 bg-green-100 px-3 py-1 rounded-full">
                95%+ Diversion Rate
              </span>
            </h3>

            <div className="bg-neutral-50 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-neutral-900 mb-2">Common Forms</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                <li>Asphalt concrete pavement (AC paving)</li>
                <li>Asphalt shingles (roofing)</li>
                <li>Asphalt millings from road work</li>
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-900 mb-2">Processing Method</h4>
                <p className="text-sm text-neutral-700">
                  Crushed and screened to remove contaminants. Recycled asphalt pavement (RAP) used in
                  hot-mix asphalt production (typically 15-30% RAP content allowed).
                </p>
              </div>
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-900 mb-2">Primary Markets</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                  <li>Hot-mix asphalt (HMA) production</li>
                  <li>Road base material</li>
                  <li>Parking lot subbase</li>
                  <li>Utility backfill</li>
                </ul>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-green-900 mb-2">Best Practices</h4>
              <ul className="space-y-2 text-green-900 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Keep separate:</strong> Don&apos;t mix with concrete &mdash; different processing/markets</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Roofing shingles:</strong> Keep separate from AC paving when possible</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Remove tar paper:</strong> Separate if required by receiving facility</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Check specs:</strong> Some HMA plants have specific RAP requirements</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Cost Considerations</h4>
              <p className="text-blue-800 text-sm">
                <strong>Tipping fees:</strong> $15&ndash;$35/ton for AC paving (cheaper than landfill).
                Roofing shingles may have higher fees due to additional processing requirements.
              </p>
            </div>
          </div>

          {/* WOOD */}
          <div className="mb-10 border-l-4 border-yellow-500 pl-6">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Wood & Lumber
              <span className="ml-3 text-sm font-normal text-yellow-700 bg-yellow-100 px-3 py-1 rounded-full">
                70-85% Clean Wood Diversion
              </span>
            </h3>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-yellow-900 mb-2">Critical Distinction: Clean vs. Treated</h4>
              <p className="text-yellow-800 text-sm mb-2">
                <strong>Clean wood</strong> (unpainted, untreated) can be recycled. <strong>Treated wood</strong>
                (painted, stained, pressure-treated) is often classified as hazardous waste due to heavy metals
                and toxic preservatives.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">Clean Wood (Recyclable)</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-green-800">
                  <li>Untreated framing lumber</li>
                  <li>Unpainted plywood and OSB</li>
                  <li>Pallets (heat-treated only)</li>
                  <li>Crates and shipping materials</li>
                  <li>Clean dimensional lumber</li>
                </ul>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-900 mb-2">Treated Wood (Hazardous)</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-red-800">
                  <li>Painted or varnished wood</li>
                  <li>Pressure-treated lumber (CCA, ACQ)</li>
                  <li>Railroad ties, utility poles</li>
                  <li>Stained decking or fencing</li>
                  <li>Wood with glue/adhesive</li>
                </ul>
              </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-neutral-900 mb-2">Processing & Markets</h4>
              <p className="text-sm text-neutral-700 mb-2">
                <strong>Clean wood:</strong> Ground into mulch, compost feedstock, biomass fuel, or engineered
                wood products.
              </p>
              <p className="text-sm text-neutral-700">
                <strong>Treated wood:</strong> Must go to licensed hazardous waste facilities or specific
                Class I landfills. Cannot be burned or ground for landscaping.
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-green-900 mb-2">Best Practices</h4>
              <ul className="space-y-2 text-green-900 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Separate rigorously:</strong> One piece of treated wood can contaminate an entire load</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Remove fasteners:</strong> Pull nails/screws when possible - increases value</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Keep dry:</strong> Wet wood adds weight and reduces quality</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Train crews:</strong> Workers must understand clean vs. treated distinction</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Salvage first:</strong> Dimensional lumber and plywood can be reused</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Cost Considerations</h4>
              <p className="text-blue-800 text-sm mb-2">
                <strong>Clean wood:</strong> $25&ndash;$50/ton tipping fee
              </p>
              <p className="text-blue-800 text-sm">
                <strong>Treated wood:</strong> $150&ndash;$400/ton at hazardous waste facilities &mdash; major cost driver
              </p>
            </div>
          </div>

          {/* DRYWALL */}
          <div className="mb-10 border-l-4 border-red-500 pl-6">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Drywall & Gypsum Board
              <span className="ml-3 text-sm font-normal text-red-700 bg-red-100 px-3 py-1 rounded-full">
                2-4% Diversion Rate - CHALLENGING
              </span>
            </h3>

            <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-red-900 mb-2">Why So Difficult?</h4>
              <p className="text-red-800 text-sm mb-2">
                Drywall recycling has very limited infrastructure in California. Most material ends up landfilled
                despite being technically recyclable. Main challenges:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-red-800">
                <li>Very few processing facilities statewide</li>
                <li>Contamination from paint, tape, texture makes processing difficult</li>
                <li>Low market value of recycled gypsum</li>
                <li>Moisture sensitivity &mdash; wet drywall unusable</li>
                <li>Transportation costs often exceed material value</li>
              </ul>
            </div>

            <div className="bg-neutral-50 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-neutral-900 mb-2">When Recycling IS Possible</h4>
              <p className="text-sm text-neutral-700 mb-2">
                <strong>New construction waste:</strong> Clean, unpainted drywall scraps can sometimes be recycled
                if you&apos;re near a facility.
              </p>
              <p className="text-sm text-neutral-700">
                <strong>Demolition waste:</strong> Painted/textured drywall is essentially non-recyclable with
                current California infrastructure.
              </p>
            </div>

            <div className="bg-white border border-neutral-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-neutral-900 mb-2">Potential Uses (when clean)</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                <li>Gypsum for agricultural soil amendment</li>
                <li>New drywall manufacturing feedstock</li>
                <li>Cement production additive</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-yellow-900 mb-2">Practical Strategies</h4>
              <ul className="space-y-2 text-yellow-900 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Don&apos;t count on it:</strong> Assume drywall will be landfilled in your diversion calculations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Separate anyway:</strong> Keep clean for potential recycling opportunities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Minimize waste:</strong> Careful cutting and planning reduces scrap generation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Check SWIS:</strong> Search CalRecycle database for nearest accepting facility</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Cost Considerations</h4>
              <p className="text-blue-800 text-sm">
                <strong>Landfill disposal:</strong> Standard $40&ndash;$80/ton. Drywall adds weight &mdash; a major cost factor.
                If recycling is available, fees similar to or slightly higher than landfill.
              </p>
            </div>
          </div>

          {/* CARDBOARD & PAPER */}
          <div className="mb-10 border-l-4 border-green-500 pl-6">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Cardboard & Paper
              <span className="ml-3 text-sm font-normal text-green-700 bg-green-100 px-3 py-1 rounded-full">
                70% Diversion Rate
              </span>
            </h3>

            <div className="bg-neutral-50 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-neutral-900 mb-2">Common Forms</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                <li>Corrugated cardboard boxes</li>
                <li>Packaging materials and void fill</li>
                <li>Paper wrapping and documentation</li>
                <li>Tubes and cores from materials</li>
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-900 mb-2">Processing Method</h4>
                <p className="text-sm text-neutral-700">
                  Baled and shipped to paper mills. Pulped and reformed into new paper products.
                  Strong recycling markets with well-established collection infrastructure.
                </p>
              </div>
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-900 mb-2">Primary Markets</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                  <li>New corrugated boxes</li>
                  <li>Paperboard and packaging</li>
                  <li>Paper products</li>
                </ul>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-green-900 mb-2">Best Practices</h4>
              <ul className="space-y-2 text-green-900 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Keep dry:</strong> Wet cardboard is worthless &mdash; protect from rain</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Flatten boxes:</strong> Saves space in bins and trucks</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Remove contaminants:</strong> Tape, labels, plastic wrap reduce quality</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Easy wins:</strong> Most waste haulers include cardboard recycling at no extra cost</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Cost Considerations</h4>
              <p className="text-blue-800 text-sm">
                <strong>Usually free or low-cost:</strong> Many waste haulers provide cardboard recycling bins
                at no additional charge. May even receive small rebates for large quantities.
              </p>
            </div>
          </div>

          {/* PLASTICS */}
          <div className="mb-10 border-l-4 border-orange-500 pl-6">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Plastics
              <span className="ml-3 text-sm font-normal text-orange-700 bg-orange-100 px-3 py-1 rounded-full">
                Limited Infrastructure - Challenging
              </span>
            </h3>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-orange-900 mb-2">The Reality</h4>
              <p className="text-orange-800 text-sm">
                Construction plastic recycling infrastructure is severely limited in California. Most C&D
                plastics are too contaminated, mixed, or low-value to economically recycle. Focus diversion
                efforts on higher-value materials.
              </p>
            </div>

            <div className="bg-neutral-50 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-neutral-900 mb-2">Common C&D Plastics</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                <li>Stretch wrap and bundling film</li>
                <li>Pipe and conduit (PVC, HDPE)</li>
                <li>Packaging materials</li>
                <li>Foam insulation</li>
                <li>Plastic buckets and containers</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-yellow-900 mb-2">Limited Opportunities</h4>
              <ul className="space-y-2 text-yellow-900 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Clean stretch wrap:</strong> Some haulers accept if clean and baled</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Reusable buckets:</strong> Donate or reuse on-site</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Source reduction:</strong> Better strategy than trying to recycle</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Check SWIS:</strong> A few specialized facilities exist for specific plastics</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Bottom Line</h4>
              <p className="text-blue-800 text-sm">
                Don&apos;t count on plastic recycling for achieving diversion targets. Focus on concrete, metal,
                wood, and cardboard for reliable diversion rates.
              </p>
            </div>
          </div>

          {/* GLASS */}
          <div className="mb-10 border-l-4 border-orange-500 pl-6">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Glass & Glazing
              <span className="ml-3 text-sm font-normal text-orange-700 bg-orange-100 px-3 py-1 rounded-full">
                Different from Container Glass
              </span>
            </h3>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-orange-900 mb-2">Important Distinction</h4>
              <p className="text-orange-800 text-sm">
                Flat glass (windows, mirrors) has different composition than container glass (bottles, jars).
                Cannot be processed together. Limited recycling infrastructure for construction flat glass.
              </p>
            </div>

            <div className="bg-neutral-50 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-neutral-900 mb-2">Common Forms</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                <li>Window panes and glazing</li>
                <li>Glass doors and storefronts</li>
                <li>Mirrors and glass shelving</li>
                <li>Tempered and laminated glass</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-yellow-900 mb-2">Recycling Options (Limited)</h4>
              <ul className="space-y-2 text-yellow-900 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Whole units:</strong> Intact windows/doors can be salvaged for reuse</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Crushed glass:</strong> Few facilities accept for aggregate or abrasive uses</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Fiberglass insulation:</strong> Different material - very limited recycling</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Practical Approach</h4>
              <p className="text-blue-800 text-sm">
                Salvage intact units when possible. Most broken glass will be landfilled. Not a significant
                volume in most projects - focus diversion efforts elsewhere.
              </p>
            </div>
          </div>

          {/* ROOFING MATERIALS */}
          <div className="mb-10 border-l-4 border-yellow-500 pl-6">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Roofing Materials
              <span className="ml-3 text-sm font-normal text-yellow-700 bg-yellow-100 px-3 py-1 rounded-full">
                Varies by Type
              </span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">Good Options</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-green-800">
                  <li>Metal roofing (95%+ diversion)</li>
                  <li>Asphalt shingles (regional facilities)</li>
                  <li>Clay/concrete tile (crushing)</li>
                </ul>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">Limited Options</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-yellow-800">
                  <li>Wood shakes (if untreated)</li>
                  <li>Membrane roofing (TPO, EPDM)</li>
                  <li>Built-up roofing (BUR)</li>
                </ul>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-900 mb-2">Difficult/Hazardous</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-red-800">
                  <li>Treated wood shakes</li>
                  <li>Asbestos shingles (hazmat)</li>
                  <li>Tar-based materials</li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-yellow-900 mb-2">Key Considerations</h4>
              <ul className="space-y-2 text-yellow-900 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Identify before removal:</strong> Test for asbestos in older buildings</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Metal roofing:</strong> Treat like other metals - high diversion rate</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Asphalt shingles:</strong> Check for nearby recycling facilities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">→</span>
                  <span><strong>Mixed materials:</strong> Separation is often impractical - landfill likely</span>
                </li>
              </ul>
            </div>
          </div>

          {/* SOIL & LAND CLEARING */}
          <div className="mb-10 border-l-4 border-green-500 pl-6">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Soil & Land Clearing Debris
              <span className="ml-3 text-sm font-normal text-green-700 bg-green-100 px-3 py-1 rounded-full">
                100% Diversion Required
              </span>
            </h3>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-blue-900 mb-2">CalGreen Requirement</h4>
              <p className="text-blue-800 text-sm">
                Land clearing debris and excavated soil MUST be counted as 100% diverted. Cannot be landfilled.
                This is your easiest path to diversion targets if you have significant site work.
              </p>
            </div>

            <div className="bg-neutral-50 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-neutral-900 mb-2">Common Materials</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                <li>Excavated soil and dirt</li>
                <li>Tree stumps and roots</li>
                <li>Brush, branches, vegetation</li>
                <li>Rocks and boulders</li>
                <li>Topsoil and organic material</li>
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-900 mb-2">Processing & Uses</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                  <li>Clean fill for grading projects</li>
                  <li>Topsoil screening and resale</li>
                  <li>Wood chips and mulch</li>
                  <li>Compost production</li>
                  <li>Erosion control materials</li>
                </ul>
              </div>
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-900 mb-2">Finding Outlets</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700">
                  <li>ADC facilities (alternative daily cover)</li>
                  <li>Landscape suppliers</li>
                  <li>Other construction sites</li>
                  <li>Landfill daily cover programs</li>
                  <li>Compost facilities</li>
                </ul>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-4">
              <h4 className="font-semibold text-green-900 mb-2">Best Practices</h4>
              <ul className="space-y-2 text-green-900 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Test soil first:</strong> Contaminated soil requires special handling and testing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Separate clean from contaminated:</strong> Mixing ruins entire load</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Keep vegetation separate:</strong> Green waste has different outlets than soil</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Plan ahead:</strong> Find accepting sites before excavation begins</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Document everything:</strong> Weight tickets required even though 100% diversion assumed</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Cost Considerations</h4>
              <p className="text-blue-800 text-sm">
                <strong>Highly variable:</strong> Free disposal at some sites needing fill. $5-$40/ton at others.
                Transportation distance is major cost factor. Contaminated soil dramatically more expensive.
              </p>
            </div>
          </div>
        </section>

        {/* PRACTICAL GUIDANCE SECTIONS */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-neutral-900 mb-6 border-b-2 border-primary-500 pb-2">
            Practical Guidance
          </h2>

          {/* FINDING FACILITIES */}
          <div className="mb-10">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Finding Recycling & Disposal Facilities
            </h3>

            <div className="bg-primary-50 border-2 border-primary-500 rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-primary-900 mb-3">CalRecycle SWIS Database</h4>
              <p className="text-primary-900 text-sm mb-3">
                The Solid Waste Information System (SWIS) is your primary tool for finding facilities.
                Search by material type, location, and service type.
              </p>
              <a
                href="https://www2.calrecycle.ca.gov/SWFacilities/Directory/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                Search SWIS Database →
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5">
                <h4 className="font-semibold text-neutral-900 mb-2">Search Tips</h4>
                <ul className="space-y-2 text-sm text-neutral-700">
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Search by material type first (e.g., &quot;concrete&quot;, &quot;metal&quot;)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Filter by distance from your project site</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Check facility status - some may be closed or full</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Verify hours and appointment requirements</span>
                  </li>
                </ul>
              </div>

              <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5">
                <h4 className="font-semibold text-neutral-900 mb-2">Before Selecting a Facility</h4>
                <ul className="space-y-2 text-sm text-neutral-700">
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Call ahead to confirm they accept your materials</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Ask about contamination limits and requirements</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Get pricing in writing - tipping fees can vary</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Confirm they provide weight tickets for documentation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* COST CONSIDERATIONS */}
          <div className="mb-10">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Cost Considerations
            </h3>

            <div className="bg-neutral-50 rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-neutral-900 mb-3">Typical California C&D Costs (2024)</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-neutral-300">
                      <th className="text-left py-2 px-3 font-semibold">Material</th>
                      <th className="text-left py-2 px-3 font-semibold">Tipping Fee</th>
                      <th className="text-left py-2 px-3 font-semibold">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="text-neutral-700">
                    <tr className="border-b border-neutral-200">
                      <td className="py-2 px-3">Landfill (mixed waste)</td>
                      <td className="py-2 px-3">$40-$80/ton</td>
                      <td className="py-2 px-3">Baseline cost - avoid when possible</td>
                    </tr>
                    <tr className="border-b border-neutral-200">
                      <td className="py-2 px-3">Concrete & masonry</td>
                      <td className="py-2 px-3">$10-$30/ton</td>
                      <td className="py-2 px-3">Major savings vs. landfill</td>
                    </tr>
                    <tr className="border-b border-neutral-200">
                      <td className="py-2 px-3">Metals</td>
                      <td className="py-2 px-3">PAID $0.10-$4.00/lb</td>
                      <td className="py-2 px-3">Revenue source - separate by type</td>
                    </tr>
                    <tr className="border-b border-neutral-200">
                      <td className="py-2 px-3">Asphalt paving</td>
                      <td className="py-2 px-3">$15-$35/ton</td>
                      <td className="py-2 px-3">Good market availability</td>
                    </tr>
                    <tr className="border-b border-neutral-200">
                      <td className="py-2 px-3">Clean wood</td>
                      <td className="py-2 px-3">$25-$50/ton</td>
                      <td className="py-2 px-3">Must be unpainted/untreated</td>
                    </tr>
                    <tr className="border-b border-neutral-200">
                      <td className="py-2 px-3">Treated wood (hazardous)</td>
                      <td className="py-2 px-3">$150-$400/ton</td>
                      <td className="py-2 px-3">Major cost driver - separate carefully</td>
                    </tr>
                    <tr className="border-b border-neutral-200">
                      <td className="py-2 px-3">Cardboard</td>
                      <td className="py-2 px-3">Free-$10/ton</td>
                      <td className="py-2 px-3">Often included in waste service</td>
                    </tr>
                    <tr className="border-b border-neutral-200">
                      <td className="py-2 px-3">Drywall (if available)</td>
                      <td className="py-2 px-3">$40-$90/ton</td>
                      <td className="py-2 px-3">Limited facility availability</td>
                    </tr>
                    <tr className="border-b border-neutral-200">
                      <td className="py-2 px-3">Clean soil/fill</td>
                      <td className="py-2 px-3">Free-$40/ton</td>
                      <td className="py-2 px-3">Highly variable by location</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5">
              <h4 className="font-semibold text-yellow-900 mb-2">Hidden Costs to Consider</h4>
              <ul className="space-y-2 text-yellow-900 text-sm">
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span><strong>Labor for separation:</strong> Additional crew time vs. single mixed dumpster</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span><strong>Multiple containers:</strong> Rental fees for separate bins</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span><strong>Transportation:</strong> Fuel and truck time to multiple facilities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span><strong>Site space:</strong> Room for multiple sorting areas</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span><strong>Contamination penalties:</strong> Loads rejected for excessive contamination</span>
                </li>
              </ul>
            </div>
          </div>

          {/* DOCUMENTATION TIPS */}
          <div className="mb-10">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Documentation Tips
            </h3>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
              <h4 className="font-semibold text-green-900 mb-3">Required Documentation</h4>
              <ul className="space-y-2 text-green-900 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Weight tickets:</strong> From every facility - keep originals</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Facility information:</strong> Name, address, license number, material accepted</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Photos:</strong> Separated materials on-site, loaded trucks, facility signage</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Calculations:</strong> Total diverted weight ÷ total waste × 100 = diversion %</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Waste management plan:</strong> Compliant plan showing intended diversion methods</span>
                </li>
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5">
                <h4 className="font-semibold text-neutral-900 mb-2">Weight Ticket Must-Haves</h4>
                <ul className="space-y-2 text-sm text-neutral-700">
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Facility name and location</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Date of delivery</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Net weight (not volume)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Material type</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Your project/company name</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Vehicle/truck identifier</span>
                  </li>
                </ul>
              </div>

              <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5">
                <h4 className="font-semibold text-neutral-900 mb-2">Record-Keeping Best Practices</h4>
                <ul className="space-y-2 text-sm text-neutral-700">
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Create project folder immediately</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Scan tickets same day as delivery</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Maintain running totals by material</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Update diversion calculation weekly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Keep backups of all documentation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Include in final permit package</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* COMMON MISTAKES */}
          <div className="mb-10">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Common Mistakes to Avoid
            </h3>

            <div className="space-y-4">
              <div className="bg-red-50 border-l-4 border-red-500 p-5">
                <h4 className="font-semibold text-red-900 mb-2">Mixing Treated & Untreated Wood</h4>
                <p className="text-red-800 text-sm mb-2">
                  <strong>The Problem:</strong> One painted 2x4 can contaminate an entire load of clean wood,
                  forcing it to hazardous waste disposal at 5&ndash;10&times; the cost.
                </p>
                <p className="text-red-800 text-sm">
                  <strong>The Solution:</strong> Train crews rigorously. Use clearly marked bins. When in doubt,
                  separate it out.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-5">
                <h4 className="font-semibold text-red-900 mb-2">Not Finding Facilities First</h4>
                <p className="text-red-800 text-sm mb-2">
                  <strong>The Problem:</strong> Starting work without confirmed outlets for materials. Discover
                  too late that nearest drywall facility is 200 miles away.
                </p>
                <p className="text-red-800 text-sm">
                  <strong>The Solution:</strong> Search SWIS database during planning. Call facilities.
                  Get pricing and requirements in writing before work starts.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-5">
                <h4 className="font-semibold text-red-900 mb-2">Relying on Difficult Materials</h4>
                <p className="text-red-800 text-sm mb-2">
                  <strong>The Problem:</strong> Planning to divert drywall and plastics to hit 65%. These have
                  2-10% real-world diversion rates due to infrastructure limits.
                </p>
                <p className="text-red-800 text-sm">
                  <strong>The Solution:</strong> Build plan around high-diversion materials: concrete (95%),
                  metals (95%), asphalt (95%), clean wood (70-85%).
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-5">
                <h4 className="font-semibold text-red-900 mb-2">Poor Documentation</h4>
                <p className="text-red-800 text-sm mb-2">
                  <strong>The Problem:</strong> Missing weight tickets. No photos. Can&apos;t prove diversion at
                  final inspection. Delayed certificate of occupancy.
                </p>
                <p className="text-red-800 text-sm">
                  <strong>The Solution:</strong> Designate one person responsible for collecting tickets.
                  Scan immediately. Take photos at each stage.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-5">
                <h4 className="font-semibold text-red-900 mb-2">Letting Materials Get Wet</h4>
                <p className="text-red-800 text-sm mb-2">
                  <strong>The Problem:</strong> Rain ruins cardboard and wood. Adds weight to everything.
                  Wet drywall worthless. Increases costs and reduces diversion.
                </p>
                <p className="text-red-800 text-sm">
                  <strong>The Solution:</strong> Cover bins. Protect sorting areas. Prioritize material removal
                  before weather events.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-5">
                <h4 className="font-semibold text-red-900 mb-2">Underestimating Contamination Penalties</h4>
                <p className="text-red-800 text-sm mb-2">
                  <strong>The Problem:</strong> Load rejected at facility. Must haul to landfill at premium rate.
                  Wasted time and money.
                </p>
                <p className="text-red-800 text-sm">
                  <strong>The Solution:</strong> Understand each facility&apos;s contamination limits (typically 5&ndash;10%).
                  Do final inspection before loading. Remove obvious contaminants.
                </p>
              </div>
            </div>
          </div>

          {/* STRATEGIC APPROACH */}
          <div className="mb-10">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
              Strategic Approach to Achieving 65%
            </h3>

            <div className="bg-primary-50 border border-primary-500 rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-primary-900 mb-3">The 80/20 Rule for C&D Diversion</h4>
              <p className="text-primary-900 text-sm mb-3">
                Focus your effort on high-volume, high-diversion-rate materials. These four categories typically
                represent 70-80% of C&D waste by weight:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-white rounded p-3">
                  <p className="font-semibold text-primary-900">1. Concrete & Masonry</p>
                  <p className="text-sm text-primary-800">95%+ diversion, 40-50% of waste</p>
                </div>
                <div className="bg-white rounded p-3">
                  <p className="font-semibold text-primary-900">2. Metals (All Types)</p>
                  <p className="text-sm text-primary-800">95%+ diversion, 5-10% of waste</p>
                </div>
                <div className="bg-white rounded p-3">
                  <p className="font-semibold text-primary-900">3. Asphalt Paving</p>
                  <p className="text-sm text-primary-800">95%+ diversion, 10-20% of waste</p>
                </div>
                <div className="bg-white rounded p-3">
                  <p className="font-semibold text-primary-900">4. Clean Wood</p>
                  <p className="text-sm text-primary-800">70-85% diversion, 15-25% of waste</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Example: Typical Demolition Project</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-green-300">
                      <th className="text-left py-2 px-3 font-semibold text-green-900">Material</th>
                      <th className="text-right py-2 px-3 font-semibold text-green-900">Weight (tons)</th>
                      <th className="text-right py-2 px-3 font-semibold text-green-900">Diversion Rate</th>
                      <th className="text-right py-2 px-3 font-semibold text-green-900">Diverted (tons)</th>
                    </tr>
                  </thead>
                  <tbody className="text-green-900">
                    <tr className="border-b border-green-200">
                      <td className="py-2 px-3">Concrete</td>
                      <td className="py-2 px-3 text-right">450</td>
                      <td className="py-2 px-3 text-right">95%</td>
                      <td className="py-2 px-3 text-right font-semibold">427.5</td>
                    </tr>
                    <tr className="border-b border-green-200">
                      <td className="py-2 px-3">Metals</td>
                      <td className="py-2 px-3 text-right">65</td>
                      <td className="py-2 px-3 text-right">98%</td>
                      <td className="py-2 px-3 text-right font-semibold">63.7</td>
                    </tr>
                    <tr className="border-b border-green-200">
                      <td className="py-2 px-3">Asphalt</td>
                      <td className="py-2 px-3 text-right">125</td>
                      <td className="py-2 px-3 text-right">95%</td>
                      <td className="py-2 px-3 text-right font-semibold">118.8</td>
                    </tr>
                    <tr className="border-b border-green-200">
                      <td className="py-2 px-3">Clean Wood</td>
                      <td className="py-2 px-3 text-right">80</td>
                      <td className="py-2 px-3 text-right">75%</td>
                      <td className="py-2 px-3 text-right font-semibold">60.0</td>
                    </tr>
                    <tr className="border-b border-green-200">
                      <td className="py-2 px-3">Treated Wood</td>
                      <td className="py-2 px-3 text-right">35</td>
                      <td className="py-2 px-3 text-right">0%</td>
                      <td className="py-2 px-3 text-right font-semibold">0</td>
                    </tr>
                    <tr className="border-b border-green-200">
                      <td className="py-2 px-3">Drywall</td>
                      <td className="py-2 px-3 text-right">95</td>
                      <td className="py-2 px-3 text-right">0%</td>
                      <td className="py-2 px-3 text-right font-semibold">0</td>
                    </tr>
                    <tr className="border-b border-green-200">
                      <td className="py-2 px-3">Cardboard</td>
                      <td className="py-2 px-3 text-right">15</td>
                      <td className="py-2 px-3 text-right">70%</td>
                      <td className="py-2 px-3 text-right font-semibold">10.5</td>
                    </tr>
                    <tr className="border-b border-green-200">
                      <td className="py-2 px-3">Mixed/Other</td>
                      <td className="py-2 px-3 text-right">135</td>
                      <td className="py-2 px-3 text-right">0%</td>
                      <td className="py-2 px-3 text-right font-semibold">0</td>
                    </tr>
                    <tr className="border-t-2 border-green-400 font-bold">
                      <td className="py-2 px-3">TOTALS</td>
                      <td className="py-2 px-3 text-right">1,000</td>
                      <td className="py-2 px-3 text-right">68%</td>
                      <td className="py-2 px-3 text-right">680.5</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-green-900 text-sm mt-4">
                <strong>Result:</strong> Achieved 68% diversion by focusing on four high-performing materials,
                exceeding the 65% requirement without recycling drywall, plastics, or treated wood.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
            Additional Resources
          </h2>
          <ul className="space-y-2 text-neutral-700">
            <li>
              <a
                href="https://www2.calrecycle.ca.gov/SWFacilities/Directory/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline font-medium"
              >
                CalRecycle SWIS Facility Database
              </a>
              <span className="text-sm ml-2">- Find recycling facilities by material and location</span>
            </li>
            <li>
              <a
                href="https://www.calrecycle.ca.gov/ConDemo/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                CalRecycle Construction & Demolition Resources
              </a>
            </li>
            <li>
              <a
                href="https://www.calrecycle.ca.gov/lgcentral/reports/diversionprogram/annualreport/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                California Jurisdiction Diversion Reports
              </a>
            </li>
            <li>
              <Link to="/docs/calgreen" className="text-primary-600 hover:text-primary-700 underline">
                CalGreen 5.408 Overview
              </Link>
            </li>
            <li>
              <Link to="/docs/faq" className="text-primary-600 hover:text-primary-700 underline">
                Frequently Asked Questions
              </Link>
            </li>
          </ul>
        </section>
      </div>

      <div className="mt-12 pt-6 border-t border-neutral-200">
        <p className="text-sm text-neutral-600 italic">
          This guide is based on current California market conditions and regulations. Costs, availability,
          and requirements vary by location and change over time. Always verify with local facilities and
          jurisdictions for project-specific guidance.
        </p>
      </div>
    </div>
  );
}